import { PIPEDB_URL } from "../../common/pipedb-url";
import { ClientData, LogEntry, MeasureData, ObjectModelData, ShareLinkData, StarParams } from "./interfaces";
import { currentImpersonation } from "../../api/impersonationRef";
import { mockClients, mockMeasurements, mockModels } from "./mock";
import { SessionData } from "./schemas";

export const IS_MOCK = false;
export function getAuth(): AuthProvider | null {
    // Implement your logic to retrieve the AuthProvider instance
    return null;
}
export class AuthProvider {
    getAccessTokenSilently: () => Promise<string>;
    code?: string;
    profileID?: string;
    sessionID?: string;
    isCookieSet: boolean = false
    exchangeDonePromise: Promise<boolean> | null = null;
    anonymous: () => boolean = () => this.code !== undefined && this.code !== '';


    constructor(getAccessTokenSilently: () => Promise<string>, code?: string, profileID?: string, sessionID?: string, isCookieSet: boolean = false) {
        this.code = code;
        this.profileID = profileID;
        this.sessionID = sessionID;
        this.isCookieSet = isCookieSet;
        this.getAccessTokenSilently = getAccessTokenSilently;
    }
    /* The `exchangeCodeForCookie` function is responsible for exchanging a provided code for a cookie
    by making a POST request to a specific endpoint ("/api/auth/cookie"). Here is a breakdown of its
    functionality: */
    exchangeCodeForCookie = async (): Promise<boolean> => {
        console.log("Exchanging code for cookie");
        if (this.isCookieSet) return true;
        if (!this.profileID) throw new Error("No profileID available");
        if (!this.code) throw new Error("No code available");
        const res = await fetch(`${PIPEDB_URL}/profiles/${this.profileID}/link/exchange`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: 'include',
            body: JSON.stringify({ code: this.code }),
        });
        if (!res.ok) throw new Error("Failed to exchange code for cookie");
        this.isCookieSet = true;
        return this.isCookieSet;
    };
    getAuthHeader = async () => {
        let headers = {
            'Content-Type': 'application/json',
        } as { [key: string]: string };
        if (this.code) {
            console.log("Waiting for code exchange to complete");
            if (!this.isCookieSet) {
                await this.exchangeCodeForCookie();
            }
            console.log("Code exchange completed, cookie should be set");
            headers = {
                ...headers,
            };
        } else if (this.isCookieSet) {
            // Cookie already set (e.g. from a previous scan session) — skip Auth0
            console.log("Using existing cookie for auth");
        } else {
            const token = await this.getAccessTokenSilently();
            if (token) {
                headers = {
                    ...headers,
                    Authorization: `Bearer ${token}`
                };
            }
        }
        if (currentImpersonation) {
            headers['X-Impersonate'] = currentImpersonation;
        }
        return headers;
    }
}


export const post = async (url: string, data: any, auth: AuthProvider) => {

    return fetch(`${PIPEDB_URL}/${url}`, {
        method: 'POST',
        headers: await auth.getAuthHeader(),
        credentials: 'include',
        body: JSON.stringify(data),
    });
}
export const put = async (url: string, data: any, auth: AuthProvider) => {

    return fetch(`${PIPEDB_URL}/${url}`, {
        method: 'PUT',
        headers: await auth.getAuthHeader(),
        credentials: 'include',
        body: JSON.stringify(data),
    });
}
export const get = async (url: string, auth: AuthProvider): Promise<Response> => {
    const headers = await auth.getAuthHeader();
    console.log("GET", `${PIPEDB_URL}/${url}`, headers);
    try {
        return await fetch(`${PIPEDB_URL}/${url}`, { headers: headers, credentials: 'include' });
    } catch (e: any) {
        // TODO improve UI error handling
        alert("Error fetching data try reloading page " + e.message);
        return new Response(JSON.stringify([]), {
            status: 500,
            headers: { 'Content-Type': 'application/json' }
        });
    }
}

export const del = async (url: string, auth: AuthProvider) => {
    const headers = await auth.getAuthHeader();
    return fetch(`${PIPEDB_URL}/${url}`, {
        method: 'DELETE',
        headers: await auth.getAuthHeader(),
        credentials: 'include'
    });
}
/* Get the current profile  */
export const getSession = async (profileID: string, sessionID: string, auth: AuthProvider): Promise<SessionData> => {
    if (IS_MOCK) {
        return {
            ID: sessionID,
            createdAt: new Date(),
            status: 'success',
            profileID: profileID
        };
    }
    const res = await get(`profiles/${profileID}/sessions/${sessionID}`, auth);

    return res.json();
}
export const getProfiles = async (auth: AuthProvider): Promise<ClientData[]> => {
    if (IS_MOCK) {
        return mockClients();
    }
    const res = await get('profiles', auth);
    return res.json();
}


export const addResource = async (profileID: string, sessionID: string, resourceName: string, data: any, auth: AuthProvider, raw = false) => {
    const res = await post(`profiles/${profileID}/sessions/${sessionID}/resources/${resourceName}`, data, auth);
    if (raw) {
        return res;
    }
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}

export const uploadImage = async (
    profileID: string,
    sessionID: string,
    view: string,
    imageDataUrl: string,
    gender: string,
    faceImg: boolean,
    auth: AuthProvider,
) => {
    const resp = await fetch(imageDataUrl);
    const blob = await resp.blob();
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';

    const formData = new FormData();
    formData.append('image', blob, `${view}.${ext}`);
    formData.append('gender', gender);
    if (faceImg) formData.append('face_img', 'true');

    const headers = await auth.getAuthHeader();
    delete headers['Content-Type'];

    return fetch(`${PIPEDB_URL}/profiles/${profileID}/sessions/${sessionID}/upload/${view}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
    });
}
export const getResource = async (profileID: string, sessionID: string, resourceName: string, auth: AuthProvider, raw = false) => {
    const res = await get(`profiles/${profileID}/sessions/${sessionID}/resources/${resourceName}`, auth);
    if (raw) {
        return res;
    }
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}

export const addProfile = async (client: ClientData, auth: AuthProvider) => {
    const res = await post('profiles', client, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}
export const getProfile = async (id: string, auth: AuthProvider): Promise<ClientData> => {
    if (IS_MOCK) {
        return (await mockClients()).find(c => c.ID === id) as ClientData;
    }
    const res = await get(`profiles/${id}`, auth);
    return res.json();
}
export const updateProfile = async (id: string, data: Partial<ClientData>, auth: AuthProvider): Promise<ClientData> => {
    const res = await put(`profiles/${id}`, data, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}
export const initSession = async (client: ClientData, auth: AuthProvider): Promise<SessionData> => {
    const task = (await (await post(`profiles/${client.ID}/sessions`, { client: client.ID }, auth)).json()) as SessionData;
    return task;
}


export const startTask = async (client: ClientData, weight: number | undefined, id: string, auth: AuthProvider) => {
    await post(`profiles/${client.ID}/sessions/${id}/resources/front_proj`, {
        scale: [2.0, 1.5],
        center: [0, 0]
    }, auth);
    await post(`profiles/${client.ID}/sessions/${id}/resources/right_proj`, {
        scale: [2.0, 1.5],
        center: [0, 0]
    }, auth);
    if (!client.height) {
        throw new Error("Client height is undefined");
    }
    await post(`profiles/${client.ID}/sessions/${id}/resources/height_input`, {
        value: Number(client.height / 100)
    }, auth);
    await post(`profiles/${client.ID}/sessions/${id}/resources/gender`, {
        value: client.gender
    }, auth);
    if (weight) {
        await post(`profiles/${client.ID}/sessions/${id}/resources/weight`, {
            value: weight
        }, auth);
    }
    return id;
}
export const getLink = async (profileID: string, auth: AuthProvider): Promise<ShareLinkData> => {
    const res = await get(`profiles/${profileID}/link/`, auth);
    return res.json();
}
export const saveLink = async (profileID: string, link: ShareLinkData, auth: AuthProvider) => {
    const res = await post(`profiles/${profileID}/link/`, link, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}
export const saveTask = async (profileID: string, sessionID: string, measurements: MeasureData, auth: AuthProvider): Promise<SessionData> => {
    const res = await post(`profiles/${profileID}/sessions/${sessionID}/save`, measurements, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}
export const getMeasurements = async (profileID: string, auth: AuthProvider): Promise<MeasureData[]> => {
    const pageSize = 100;
    let page = 1;
    const allMeasurements: MeasureData[] = [];

    while (true) {
        const res = await get(`profiles/${profileID}/measurements?page=${page}&pageSize=${pageSize}`, auth);
        const measurementsPage = await res.json() as MeasureData[];
        allMeasurements.push(...measurementsPage);

        if (measurementsPage.length < pageSize) {
            break;
        }
        page += 1;
    }

    return allMeasurements.sort((a, b) => {
        const aTime = new Date(a.createdAt || 0).getTime();
        const bTime = new Date(b.createdAt || 0).getTime();
        return bTime - aTime;
    });
}
export const getMeasurement = async (profileID: string, measurementID: string, auth: AuthProvider): Promise<MeasureData | null> => {
    const res = await get(`profiles/${profileID}/measurements/${measurementID}`, auth);
    if (res.status === 404) return null;
    if (!res.ok) return null;
    return res.json();
}
export const patchMeasurement = async (profileID: string, measurementId: string, data: Partial<MeasureData>, auth: AuthProvider): Promise<MeasureData> => {
    const res = await fetch(`${PIPEDB_URL}/profiles/${profileID}/measurements/${measurementId}`, {
        method: 'PATCH',
        headers: await auth.getAuthHeader(),
        body: JSON.stringify(data),
    });
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}
export const deleteMeasurement = async (profileID: string, measurementId: string, auth: AuthProvider): Promise<Response> => {
    const res = await del(`profiles/${profileID}/measurements/${measurementId}`, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res;
}
export const deleteClient = async (profileID: string, auth: AuthProvider): Promise<Response> => {
    const res = await del(`profiles/${profileID}`, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res;
}

export const getModels = async (profileID: string, auth: AuthProvider): Promise<ObjectModelData[]> => {
    const res = await get(`models/${profileID}/`, auth);
    return res.json();
}
export const getModel = async (profileID: string, taskId: string, auth: AuthProvider): Promise<String> => {

    const res = await get(`profiles/${profileID}/measurements/${taskId}/model`, auth);
    return res.text();
}
export const getStarParams = async (profileID: string, taskId: string, auth: AuthProvider): Promise<StarParams> => {
    const res = await get(`models/${profileID}/${taskId}_params.json`, auth);
    return res.json();
}
export const generateModel = async (profileID: string, sessionID: string, percentage: number, auth: AuthProvider) => {
    const res = await get(`profiles/${profileID}/measurements/${sessionID}/generate?bodyFat=${percentage}`, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res.json();
}
export const setTarget = async (clientId: string, sessionID: string, target: MeasureData['measurements'], auth: AuthProvider) => {
    const res = await post(`profiles/${clientId}/${sessionID}/target`, target, auth);
    if (res.status !== 200) {
        throw new Error(JSON.stringify(await res.json()));
    }
    return res
}




export const postLog = async (data: LogEntry, auth: AuthProvider) => {
    const res = await post('log', data, auth);
    if (res.status !== 200) {
        console.error(await res.text());
    }
    return
}
export const onShare = async (enabled: boolean, clientId: string, auth: AuthProvider): Promise<ShareLinkData> => {
    const shareLinkData = {
        ID: '',
        account: '',
        profileID: '',
        createdAt: new Date(),
        code: '',
        active: enabled

    } as ShareLinkData;

    const link = await saveLink(clientId, shareLinkData, auth);
    console.log("Share link saved: ", link);
    return link
}
